/**
 * Map Preview component
 *
 * Превью карты для галереи. Если у карты задано превью-изображение
 * (`previewImageUrl`), миниатюрой служит это изображение (Требование 9.5);
 * иначе рисуется статичный рендер: фоновый цвет карты, граница единичного диска
 * (модель Пуанкаре) и контур фундаментальной области по её вершинам (фолбэк,
 * Требования 9.6, 9.7, 11.1). Статичный рендер лёгкий — WebGL не требуется,
 * достаточно Canvas 2D.
 *
 * Клик по превью открывает экран интерактивного просмотра карты (`/map/:id`).
 *
 * Логика разбора ответа `GET /api/maps`, проекции вершин диска в координаты
 * холста и выбора источника миниатюры вынесена в чистые функции
 * ({@link parseMapsResponse}, {@link computeThumbnailGeometry},
 * {@link resolveThumbnailSource}) — они покрываются юнит-тестами.
 *
 * Требования: 9.5, 9.6, 9.7, 11.1, 11.2, 11.3
 */

import type { Complex, GeometryType, MapConfig } from '@langton/shared';

/** Точка в координатах холста (пиксели). */
export interface ThumbnailPoint {
  x: number;
  y: number;
}

/** Геометрия миниатюры фундаментальной области в координатах холста. */
export interface ThumbnailGeometry {
  /** Центр единичного диска по X (пиксели). */
  cx: number;
  /** Центр единичного диска по Y (пиксели). */
  cy: number;
  /** Радиус единичного диска (пиксели). */
  radius: number;
  /** Спроецированные вершины многоугольника (пиксели). */
  polygon: ThumbnailPoint[];
}

/**
 * Проверить, что значение — конечное число.
 */
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Проверить, что значение похоже на точку {@link Complex} с конечными `re`/`im`.
 */
function isComplexLike(value: unknown): value is Complex {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const p = value as { re?: unknown; im?: unknown };
  return isFiniteNumber(p.re) && isFiniteNumber(p.im);
}

/**
 * Разобрать один элемент списка карт (`GET /api/maps`) в {@link MapConfig}.
 * Возвращает `null`, если запись некорректна (нет идентификатора или вершин).
 * Необязательные поля приводятся к разумным значениям по умолчанию.
 */
export function parseMapSummary(entry: unknown): MapConfig | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const e = entry as Record<string, unknown>;

  if (typeof e.id !== 'string' || e.id === '') {
    return null;
  }

  const rawPolygon = e.polygon;
  if (!rawPolygon || typeof rawPolygon !== 'object') {
    return null;
  }
  const polygonObj = rawPolygon as Record<string, unknown>;
  const vertices = polygonObj.vertices;
  if (!Array.isArray(vertices) || vertices.length < 2 || !vertices.every(isComplexLike)) {
    return null;
  }

  const geometryType: GeometryType =
    e.geometryType === 'euclidean' || e.geometryType === 'spherical'
      ? e.geometryType
      : 'hyperbolic';

  return {
    id: e.id,
    name: typeof e.name === 'string' ? e.name : 'Карта',
    geometryType,
    polygon: {
      sides: isFiniteNumber(polygonObj.sides) ? polygonObj.sides : vertices.length,
      preset: typeof polygonObj.preset === 'string' ? polygonObj.preset : undefined,
      vertices: (vertices as Complex[]).map((v) => ({ re: v.re, im: v.im })),
      angleSum: isFiniteNumber(polygonObj.angleSum) ? polygonObj.angleSum : 0,
    },
    sidePairing: {
      word:
        e.sidePairing && typeof e.sidePairing === 'object' &&
        typeof (e.sidePairing as Record<string, unknown>).word === 'string'
          ? ((e.sidePairing as Record<string, unknown>).word as string)
          : '',
    },
    scale: isFiniteNumber(e.scale) && e.scale > 0 ? e.scale : 1,
    backgroundColor: typeof e.backgroundColor === 'string' ? e.backgroundColor : '#000000',
    visible: e.visible !== false,
    // Множитель максимального отдаления (Требование 2). По умолчанию 1.0.
    maxZoomOut: isFiniteNumber(e.maxZoomOut) && e.maxZoomOut > 0 ? e.maxZoomOut : 1,
    // Админский множитель максимального отдаления (Требование 11). По умолчанию 1.0.
    adminMaxZoomOut:
      isFiniteNumber(e.adminMaxZoomOut) && e.adminMaxZoomOut > 0 ? e.adminMaxZoomOut : 1,
    // Глубина прорисовки копий (Требование 12). По умолчанию 8 (Требование 12.6).
    copyDepth:
      isFiniteNumber(e.copyDepth) && Number.isInteger(e.copyDepth) && e.copyDepth >= 1
        ? e.copyDepth
        : 8,
    // Ссылка на превью-изображение (Требование 9). Пустая строка трактуется как «не задано».
    previewImageUrl:
      typeof e.previewImageUrl === 'string' && e.previewImageUrl.trim() !== ''
        ? e.previewImageUrl
        : undefined,
  };
}

/**
 * Разобрать тело ответа `GET /api/maps` в список видимых карт.
 *
 * Чистая функция без DOM — единый источник истины для формирования данных
 * превью; покрывается юнит-тестами. Некорректные записи отбрасываются; скрытые
 * карты (`visible === false`) исключаются (защитно — сервер уже их не отдаёт,
 * Требование 11.3).
 *
 * @param json разобранный JSON ответа сервера
 * @returns список видимых карт
 */
export function parseMapsResponse(json: unknown): MapConfig[] {
  if (!Array.isArray(json)) {
    return [];
  }
  const maps: MapConfig[] = [];
  for (const entry of json) {
    const map = parseMapSummary(entry);
    if (map && map.visible) {
      maps.push(map);
    }
  }
  return maps;
}

/**
 * Спроецировать вершины фундаментального многоугольника (координаты диска,
 * `|z| ≤ 1`) в координаты холста и вычислить границу единичного диска.
 *
 * Чистая функция (без DOM/Canvas) — покрывается юнит-тестами. Диск вписывается
 * в холст с учётом отступа; ось `im` инвертируется, так как экранная ось Y
 * направлена вниз.
 *
 * @param vertices вершины многоугольника в координатах диска
 * @param width ширина холста (пиксели)
 * @param height высота холста (пиксели)
 * @param padding отступ диска от края холста (пиксели)
 * @returns геометрия миниатюры в координатах холста
 */
export function computeThumbnailGeometry(
  vertices: Complex[],
  width: number,
  height: number,
  padding = 6,
): ThumbnailGeometry {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.max(0, Math.min(width, height) / 2 - padding);
  const polygon: ThumbnailPoint[] = vertices.map((v) => ({
    x: cx + v.re * radius,
    y: cy - v.im * radius,
  }));
  return { cx, cy, radius, polygon };
}

/**
 * Источник миниатюры карты в галерее: либо заданное превью-изображение, либо
 * статичный рендер фундаментальной области.
 */
export type ThumbnailSource =
  | { readonly kind: 'image'; readonly url: string }
  | { readonly kind: 'render' };

/**
 * Определить, что показывать в качестве миниатюры карты (Требование 9.5, 9.6, 9.7).
 *
 * Чистая функция без DOM — единственный источник истины для ветвления фолбэка,
 * покрывается юнит- и property-тестами (Свойство 26). Если у карты задано
 * непустое `previewImageUrl`, миниатюра — это изображение; иначе используется
 * статичный рендер фундаментальной области. Удаление превью (`previewImageUrl`
 * снова не задан) автоматически возвращает карту к статичному рендеру.
 *
 * @param config конфигурация карты
 * @returns дескриптор источника миниатюры
 */
export function resolveThumbnailSource(config: Pick<MapConfig, 'previewImageUrl'>): ThumbnailSource {
  const url = config.previewImageUrl;
  if (typeof url === 'string' && url.trim() !== '') {
    return { kind: 'image', url };
  }
  return { kind: 'render' };
}

/**
 * Получить контрастный цвет (чёрный/белый) для читаемости поверх фона.
 */
function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  if (hex.length < 6) {
    return '#FFFFFF';
  }
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/** Человекочитаемое название типа геометрии. */
function getGeometryTypeLabel(geometryType: GeometryType): string {
  switch (geometryType) {
    case 'hyperbolic':
      return 'Гиперболическая';
    case 'euclidean':
      return 'Евклидова';
    case 'spherical':
      return 'Сферическая';
    default:
      return geometryType;
  }
}

/**
 * MapPreview — карточка превью карты в галерее.
 *
 * Требования:
 * - 11.1: карта показывается в галерее как отдельный элемент (фон + миниатюра
 *   фундаментальной области);
 * - 11.2: клик открывает экран просмотра карты `/map/:id`.
 */
export class MapPreview {
  private element: HTMLElement;
  /**
   * Холст статичного рендера. `null`, когда миниатюра показывается как
   * превью-изображение (Требование 9.5) и статичный рендер не нужен.
   */
  private canvas: HTMLCanvasElement | null = null;
  private config: MapConfig;
  private onClick: (id: string) => void;

  constructor(config: MapConfig, onClick: (id: string) => void) {
    this.config = config;
    this.onClick = onClick;
    this.element = this.createPreviewElement();
    // Статичный рендер нужен только при фолбэке (нет превью-изображения).
    if (this.canvas) {
      this.render();
    }
  }

  /**
   * Создать DOM-элемент превью (холст миниатюры + метаданные карты).
   * Стилистика совпадает с {@link SimulationPreview} (Требования 11.1, 11.2).
   */
  private createPreviewElement(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'map-preview';
    container.style.cssText = `
      background-color: transparent;
      border: 2px solid #FFFFFF;
      border-radius: 12px;
      padding: 16px;
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
      position: relative;
      z-index: 1;
    `;

    // Hover-эффекты (как у превью симуляций).
    container.addEventListener('mouseenter', () => {
      container.style.transform = 'scale(1.02)';
      container.style.boxShadow = '0 0 15px rgba(255, 255, 255, 0.4), 0 0 30px rgba(255, 255, 255, 0.2)';
    });
    container.addEventListener('mouseleave', () => {
      container.style.transform = 'scale(1)';
      container.style.boxShadow = 'none';
    });

    // Клик открывает экран просмотра карты (Требование 11.2).
    container.addEventListener('click', () => {
      this.onClick(this.config.id);
    });

    // Миниатюра: превью-изображение (Требование 9.5) либо статичный рендер
    // фундаментальной области как фолбэк (Требования 9.6, 11.1).
    container.appendChild(this.createThumbnailElement());

    container.appendChild(this.createMetadataElement());

    return container;
  }

  /**
   * Создать элемент миниатюры карты.
   *
   * Требования 9.5, 9.6, 9.7: если у карты задано `previewImageUrl`, миниатюра —
   * это изображение (`<img>`); иначе — статичный рендер фундаментальной области
   * на `<canvas>` (существующее поведение, фолбэк). Если превью-изображение не
   * загрузилось, миниатюра также деградирует к статичному рендеру.
   */
  private createThumbnailElement(): HTMLElement {
    const source = resolveThumbnailSource(this.config);
    if (source.kind === 'image') {
      const img = document.createElement('img');
      img.className = 'preview-image';
      img.src = source.url;
      img.alt = this.config.name;
      img.style.cssText = `
        width: 100%;
        aspect-ratio: 3 / 2;
        border: 2px solid #FFFFFF;
        border-radius: 8px;
        display: block;
        box-sizing: border-box;
        object-fit: cover;
      `;
      // Если изображение не загрузилось — деградировать к статичному рендеру.
      img.addEventListener('error', () => {
        const canvas = this.createCanvasThumbnail();
        img.replaceWith(canvas);
        this.canvas = canvas;
        this.render();
      });
      return img;
    }

    const canvas = this.createCanvasThumbnail();
    this.canvas = canvas;
    return canvas;
  }

  /**
   * Создать холст миниатюры фундаментальной области (Требование 11.1).
   */
  private createCanvasThumbnail(): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.className = 'preview-canvas';
    canvas.width = 200;
    canvas.height = 133;
    canvas.style.cssText = `
      width: 100%;
      aspect-ratio: 3 / 2;
      border: 2px solid #FFFFFF;
      border-radius: 8px;
      display: block;
      box-sizing: border-box;
    `;
    return canvas;
  }

  /** Создать блок метаданных карты (название и тип геометрии). */
  private createMetadataElement(): HTMLElement {
    const metadata = document.createElement('div');
    metadata.className = 'preview-metadata';
    metadata.style.cssText = `
      margin-top: 12px;
      color: #FFFFFF;
      font-family: 'Arial', sans-serif;
    `;

    const name = document.createElement('h3');
    name.className = 'preview-name';
    name.textContent = this.config.name;
    name.style.cssText = `
      margin: 0 0 8px 0;
      font-size: 1.2rem;
      font-weight: 600;
    `;
    metadata.appendChild(name);

    const info = document.createElement('div');
    info.className = 'preview-grid-info';
    info.style.cssText = `
      display: flex;
      gap: 12px;
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.7);
    `;

    const kind = document.createElement('span');
    kind.className = 'preview-kind';
    kind.textContent = 'Карта';
    info.appendChild(kind);

    const geometry = document.createElement('span');
    geometry.className = 'preview-geometry-type';
    geometry.textContent = getGeometryTypeLabel(this.config.geometryType);
    info.appendChild(geometry);

    metadata.appendChild(info);

    return metadata;
  }

  /**
   * Отрисовать статичную миниатюру: фон карты, границу диска и контур
   * фундаментального многоугольника (Требование 11.1).
   */
  private render(): void {
    if (!this.canvas) {
      return;
    }
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    const { width, height } = this.canvas;

    // Фон карты (Требование 11.1).
    ctx.fillStyle = this.config.backgroundColor || '#000000';
    ctx.fillRect(0, 0, width, height);

    const accent = getContrastColor(this.config.backgroundColor || '#000000');
    const geo = computeThumbnailGeometry(this.config.polygon.vertices, width, height);

    if (geo.radius <= 0) {
      return;
    }

    // Граница единичного диска (модель Пуанкаре).
    ctx.strokeStyle = accent === '#000000' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(geo.cx, geo.cy, geo.radius, 0, Math.PI * 2);
    ctx.stroke();

    // Контур фундаментальной области.
    if (geo.polygon.length >= 2) {
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(geo.polygon[0].x, geo.polygon[0].y);
      for (let i = 1; i < geo.polygon.length; i++) {
        ctx.lineTo(geo.polygon[i].x, geo.polygon[i].y);
      }
      ctx.closePath();
      ctx.stroke();
    }
  }

  /** DOM-элемент превью. */
  getElement(): HTMLElement {
    return this.element;
  }

  /** Конфигурация карты, отображаемой в превью. */
  getConfig(): MapConfig {
    return this.config;
  }

  /** Освободить ресурсы (превью статично — очищать нечего). */
  destroy(): void {
    // Нет анимации или подписок: метод предоставлен для единообразия API.
  }
}
